import React, { useState, useCallback, ReactElement } from 'react';
import * as css from './PastRecalls.module.scss';
import 'src/css/themes.scss';
import { IconSeeMore, IconSortAscending, IconSortDescending } from 'src/content/icons';
import { ConnectedVehicleRec, CAMPAIGN } from 'src/models';
import { Dialog, ScrollBar} from '@framework-controls/core';
import { strings } from 'src/localization';

export interface IProps {
    connectedVehicle: ConnectedVehicleRec;
}

const PastRecalls = (props: IProps) => {
    const [recallsOpen, setRecallsOpen] = useState(false);
    const [sortReversed, setSortReversed] = useState(false);
    const toggleRecalls = useCallback(() => {
        setRecallsOpen(!recallsOpen);
    }, [recallsOpen]);

    const reverseSort = useCallback(() => {
        setSortReversed(!sortReversed);
    }, [sortReversed]);

    const [sortIconColumn, setSortIconColumn] = useState('none');

    const moveFocusToDesc = useCallback(() => {
        if (sortIconColumn !== 'desc') {
            setSortReversed(false);
        }
        setSortIconColumn('desc');
    }, [sortIconColumn]);

    const moveFocusToCode = useCallback(() => {
        if (sortIconColumn !== 'code') {
            setSortReversed(false);
        }
        setSortIconColumn('code');
    }, [sortIconColumn]);

    const moveFocusToDate = useCallback(() => {
        if (sortIconColumn !== 'date') {
            setSortReversed(false);
        }
        setSortIconColumn('date');
    }, [sortIconColumn]);

    const moveFocusToStatus = useCallback(() => {
        if (sortIconColumn !== 'status') {
            setSortReversed(false);
        }
        setSortIconColumn('status');
    }, [sortIconColumn]);

    const closedCamp: CAMPAIGN[] = [];
    props.connectedVehicle.campaign.forEach((camp) => {
        if ((camp.status !== 'Not Completed')) {
            closedCamp.push(
                {
                    code: camp.code,
                    desc: camp.desc,
                    remedy: camp.remedy,
                    remedyAvailable: camp.remedyAvailable,
                    stopSale: camp.stopSale,
                    stopCode: camp.stopCode,
                    startDate: camp.startDate,
                    bulletinId: camp.bulletinId,
                    bulletinUrl: camp.bulletinUrl,
                    recallId: camp.recallId,
                    recallUrl: camp.recallUrl,
                    reservedBy: camp.reservedBy,
                    status: camp.status,
                    repairTime: camp.repairTime,
                    partsReq: camp.partsReq,
                    index: camp.index,
                },
            );
        }
    });

    let recalls: ReactElement[] | null = null;
    if ((props.connectedVehicle.parentMake === 'TY' || props.connectedVehicle.parentMake === 'LX') &&
        closedCamp.length > 0) {
        let sortedRecalls: CAMPAIGN[] = [];
        const indexedRecalls: CAMPAIGN[] = [];
        if ((sortIconColumn === 'none') || (sortIconColumn === 'desc')) {
            sortedRecalls = closedCamp.sort((a, b) => (a.desc > b.desc ? 1 : -1));
            if (sortReversed) {
                sortedRecalls = closedCamp.sort((a, b) => (b.desc > a.desc ? 1 : -1));
            }
        }
        if (sortIconColumn === 'code') {
            sortedRecalls = closedCamp.sort((a, b) => (a.code > b.code ? 1 : -1));
            if (sortReversed) {
                sortedRecalls = closedCamp.sort((a, b) => (b.code > a.code ? 1 : -1));
            }
        }
        if (sortIconColumn === 'date') {
            sortedRecalls = closedCamp.sort((a, b) => (a.startDate > b.startDate ? 1 : -1));
            if (sortReversed) {
                sortedRecalls = closedCamp.sort((a, b) => (b.startDate > a.startDate ? 1 : -1));
            }
        }
        if (sortIconColumn === 'status') {
            sortedRecalls = closedCamp.sort((a, b) => (a.status > b.status ? 1 : -1));
            if (sortReversed) {
                sortedRecalls = closedCamp.sort((a, b) => (b.status > a.status ? 1 : -1));
            }
        }
        let index: number = 0;
        sortedRecalls.forEach((recall) => {
            index = index + 1;
            indexedRecalls.push(
                {
                    index: index,
                    desc: recall.desc,
                    code: recall.code,
                    startDate: recall.startDate,
                    status: recall.status,
                    remedy: '',
                    remedyAvailable: '',
                    stopSale: '',
                    stopCode: '',
                    bulletinId: '',
                    bulletinUrl: '',
                    recallId: '',
                    recallUrl: '',
                    reservedBy: '',
                    repairTime: '',
                    partsReq: '',
                },
            );
        });
        recalls = indexedRecalls.map(recall => <span className={recall.index % 2 === 0 ? css.recallRow : css.recallRowOdd}
            id={recall.code}
            key={recall.code}>
            <div className={css.recallDesc}>
                <span className={css.recallDescText}>{recall.desc}</span>
            </div>
            <div className={css.recallCode}>{recall.code}</div>
            <div className={css.recallDate}>{recall.startDate}</div>
            <div className={css.recallStatus}>{recall.status}</div>
        </span>);
    }

    let descSortIcon: ReactElement | null = null;
    if (sortIconColumn === 'desc') {
        descSortIcon = <IconSortAscending id='descSortIcon' className={css.recallIcon} onClick={() => reverseSort()}/>;
        if (sortReversed) {
            descSortIcon = <IconSortDescending id='descSortIcon2' className={css.recallIcon} onClick={() => reverseSort()}/>;
        }
    }

    let codeSortIcon: ReactElement | null = null;
    if (sortIconColumn === 'code') {
        codeSortIcon = <IconSortAscending id='codeSortIcon' className={css.recallIcon} onClick={() => reverseSort()}/>;
        if (sortReversed) {
            codeSortIcon = <IconSortDescending id='codeSortIcon2' className={css.recallIcon} onClick={() => reverseSort()}/>;
        }
    }

    let dateSortIcon: ReactElement | null = null;
    if (sortIconColumn === 'date') {
        dateSortIcon = <IconSortAscending id='dateSortIcon' className={css.recallIcon} onClick={() => reverseSort()}/>;
        if (sortReversed) {
            dateSortIcon = <IconSortDescending id='dateSortIcon2' className={css.recallIcon} onClick={() => reverseSort()}/>;
        }
    }

    let statusSortIcon: ReactElement | null = null;
    if (sortIconColumn === 'status') {
        statusSortIcon = <IconSortAscending id='statusSortIcon' className={css.recallIcon} onClick={() => reverseSort()}/>;
        if (sortReversed) {
            statusSortIcon = <IconSortDescending id='statusSortIcon2' className={css.recallIcon} onClick={() => reverseSort()}/>;
        }
    }

    let pastRecallArea: ReactElement | null = null;
    if ((props.connectedVehicle.parentMake === 'TY' || props.connectedVehicle.parentMake === 'LX') &&
         (props.connectedVehicle.campaign.some(result => (result.status !== 'Not Completed')))) {
        pastRecallArea =
        <div>
            <span className={css.recallHeader}>
                <div className={css.recallDescArea}>
                    <div id='recallDescLit' className={css.recallDescLit} onClick={moveFocusToDesc}>
                        {strings.Description}
                    </div>
                    {descSortIcon}
                </div>

                <div className={css.recallCodeArea}>
                    <div id='recallCodeLit' className={css.recallCodeLit} onClick={moveFocusToCode}>
                        {strings.CampaignCode}
                    </div>
                    {codeSortIcon}
                </div>

                <div className={css.recallDateArea}>
                    <div id='recallDateLit' className={css.recallDateLit} onClick={moveFocusToDate}>
                        {strings.StartDate}
                    </div>
                    {dateSortIcon}
                </div>

                <div className={css.recallStatusArea}>
                    <div id='recallStatusLit' className={css.recallStatusLit} onClick={moveFocusToStatus}>
                        {strings.RecallStatus}
                    </div>
                    {statusSortIcon}
                </div>
            </span>
            <ScrollBar scrollVertical>
                <div className={css.recalls}>
                    {recalls}
                </div>
            </ScrollBar>
        </div>;
    }

    return (
        <div>
            <span className={css.header}>
                <div className={css.headerLit}>{strings.ViewPastRecalls}</div>
                <IconSeeMore className={css.icon} onClick={() => toggleRecalls()} />
            </span>
            <Dialog
                cssClasses={{
                    content: css.content,
                    paper: css.paper,
                    header: css.LightBoxHeader,
                }}
                isOpen={recallsOpen}
                anchorOrigin={{
                    vertical: 'top',
                }}
                header={strings.PastRecalls}
                hideSave={true}
                hideCancel={true}
                disableUpperCase={true}
                onCancel={() => toggleRecalls()}
                onBackDropClick={() => toggleRecalls()}
                width={'full'}
            >
                <div>
                    {pastRecallArea}
                </div>
            </Dialog>
        </div>
    );
};

export default PastRecalls;